import { useEffect } from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Tooltip,
  Zoom,
  Dialog,
  Portal,
} from "@mui/material";
import { IconTypes } from "../../components/icons.types";
import { CopilotWidget } from "../copilot/CopilotWidget";
import { useAtom } from "jotai";
import { sessionAtom } from "../../features/accounts/account.state";
import { helpPanelStateAtom } from "../copilot/copilotAtoms";
import { useCopilotChat } from "../copilot/useCopilotChat";
import { trackEvent } from "../../features/analytics/analytics";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  hideIntercomLauncher,
  showIntercomLauncher,
} from "../../services/intercom";

type HelpState = "collapsed" | "panel" | "modal";

// Routes where AI Help button AND Intercom launcher should be hidden
const EXCLUDED_ROUTES = ["/start", "/login", "/signup", "/concierge", "/siteadmin", "/support"];

const HelpButton = () => {
  const [session] = useAtom(sessionAtom);
  const [helpState, setHelpState] = useAtom(helpPanelStateAtom);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if current route should hide help button
  const shouldHideHelp = EXCLUDED_ROUTES.some(
    (route) =>
      location.pathname === route || location.pathname.startsWith(route + "/"),
  );

  // Deep link from help articles: ?copilot=on auto-opens the AI Help panel.
  // Strips the param after opening so the URL stays clean.
  useEffect(() => {
    if (
      searchParams.get("copilot") === "on" &&
      !shouldHideHelp &&
      session?.account
    ) {
      setHelpState("panel");
      trackEvent({
        type: "trace",
        category: "ai-copilot",
        action: "ai-help-deeplink-open",
        label: location.pathname,
        internal: true,
      });
      const next = new URLSearchParams(searchParams);
      next.delete("copilot");
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    setHelpState,
    shouldHideHelp,
    session?.account,
    location.pathname,
  ]);

  // Initialize copilot chat
  const {
    messages,
    loading,
    error,
    conversationId,
    sendMessage,
    sendFeedback,
    reset,
    loadConversation,
  } = useCopilotChat({});

  // Helper to track help state changes
  const handleStateChange = (newState: HelpState) => {
    const oldState = helpState;

    // Track state transitions
    if (oldState === "collapsed" && newState === "panel") {
      trackEvent({
        type: "trace",
        category: "ai-copilot",
        action: "ai-help-open",
        label: location.pathname,
        metadata: {
          pageUrl: location.pathname,
          openedAs: "panel",
        },
        internal: true,
      });
    } else if (oldState === "panel" && newState === "modal") {
      trackEvent({
        type: "trace",
        category: "ai-copilot",
        action: "ai-help-expand",
        label: location.pathname,
        internal: true,
      });
    } else if (oldState === "modal" && newState === "panel") {
      trackEvent({
        type: "trace",
        category: "ai-copilot",
        action: "ai-help-collapse",
        label: location.pathname,
        internal: true,
      });
    } else if (newState === "collapsed") {
      trackEvent({
        type: "trace",
        category: "ai-copilot",
        action: "ai-help-close",
        label: location.pathname,
        metadata: {
          closedFrom: oldState,
        },
        internal: true,
      });
    }

    setHelpState(newState);
  };

  // Reset copilot chat after Concierge handoff (action:concierge sets this flag)
  useEffect(() => {
    if (sessionStorage.getItem('copilot_needs_reset')) {
      sessionStorage.removeItem('copilot_needs_reset');
      reset();
    }
  }, [location.pathname, reset]);

  // Hide Intercom launcher on excluded routes (siteadmin, support, concierge, etc.)
  useEffect(() => {
    if (shouldHideHelp) {
      hideIntercomLauncher();
      return () => {
        showIntercomLauncher();
      };
    }
  }, [shouldHideHelp]);

  // Panel stays open when clicking outside - users may want to read and interact with page
  // Removed click-outside handler to keep panel persistent until user explicitly closes it

  if (!session?.account || shouldHideHelp) {
    return null;
  }

  return (
    <>
      {/* Floating buttons */}
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 80,
          zIndex: (theme) => theme.zIndex.modal + 1,
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        {/* Main AI Help button - visible only when collapsed */}
        {helpState === "collapsed" && (
          <Zoom in>
            <Paper
              elevation={4}
              sx={{
                backgroundColor: "primary.main",
                borderRadius: 2,
                overflow: "hidden",
                "&:hover": {
                  boxShadow: 6,
                },
              }}
            >
              <Tooltip title="Get help from AI" placement="left">
                <IconButton
                  size="large"
                  onClick={() => handleStateChange("panel")}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "primary.contrastText",
                  }}
                >
                  <IconTypes.Chat />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    AI Help
                  </Typography>
                </IconButton>
              </Tooltip>
            </Paper>
          </Zoom>
        )}

        {/* Minimize button - visible when panel open */}
        {helpState === "panel" && (
          <Zoom in>
            <Paper
              elevation={4}
              sx={{
                backgroundColor: "primary.main",
                borderRadius: "50%",
                overflow: "hidden",
                "&:hover": {
                  boxShadow: 6,
                },
              }}
            >
              <Tooltip title="Minimize AI Help" placement="left">
                <IconButton
                  size="large"
                  onClick={() => handleStateChange("collapsed")}
                  sx={{
                    color: "primary.contrastText",
                  }}
                >
                  <IconTypes.ArrowDown />
                </IconButton>
              </Tooltip>
            </Paper>
          </Zoom>
        )}
      </Box>

      {/* Modal state (fullscreen overlay) */}
      {helpState === "modal" && (
        <Dialog
          open={true}
          onClose={() => handleStateChange("panel")}
          maxWidth="lg"
          fullWidth
          disableEscapeKeyDown={false} // Allow ESC to close
          PaperProps={{
            sx: {
              height: "90vh",
              maxHeight: "90vh",
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconTypes.Chat />
              <Typography variant="h6">AI Help</Typography>
            </Box>

            <IconButton
              onClick={() => handleStateChange("panel")}
              sx={{ color: "inherit" }}
            >
              <IconTypes.Close />
            </IconButton>
          </Box>

          {/* Widget */}
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <CopilotWidget
              size="fullscreen"
              height="100%"
              defaultTab="conversation"
              messages={messages}
              loading={loading}
              error={error}
              conversationId={conversationId}
              onSendMessage={sendMessage}
              onSendFeedback={sendFeedback}
              onReset={reset}
              onLoadConversation={loadConversation}
            />
          </Box>
        </Dialog>
      )}

      {/* Panel state (bottom-right slide-up) */}
      {helpState === "panel" && (
        <Portal container={() => document.getElementById("ai-help-portal")}>
          <Paper
            elevation={8}
            sx={{
              position: "fixed",
              bottom: 80,
              right: 20,
              width: 450,
              height: "calc(100vh - 140px)",
              zIndex: (theme) => theme.zIndex.modal + 100, // Above everything
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconTypes.Chat />
                <Typography variant="h6">AI Help</Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Expand to fullscreen">
                  <IconButton
                    size="small"
                    onClick={() => handleStateChange("modal")}
                    sx={{ color: "inherit" }}
                  >
                    <IconTypes.OpenInNew />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton
                    size="small"
                    onClick={() => handleStateChange("collapsed")}
                    sx={{ color: "inherit" }}
                  >
                    <IconTypes.Close />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* CopilotWidget */}
            <Box sx={{ flex: 1, overflow: "hidden" }}>
              <CopilotWidget
                size="embedded"
                height="100%"
                showFeedback={true}
                defaultTab="conversation"
                messages={messages}
                loading={loading}
                error={error}
                conversationId={conversationId}
                onSendMessage={sendMessage}
                onSendFeedback={sendFeedback}
                onReset={reset}
                onLoadConversation={loadConversation}
              />
            </Box>
          </Paper>
        </Portal>
      )}
    </>
  );
};

export default HelpButton;
